<template>
  <div class="space-y-4">
    <!-- Guide Search and Selection -->
    <FormField name="guideSearch" v-if="canEdit">
      <FormItem>
        <Combobox v-model="comboboxModel" @update:model-value="handleGuideSelection" by="id">
          <ComboboxAnchor as-child class="max-w-full w-[400px]">
            <ComboboxTrigger
              class="text-left border-gray-300 rounded-md border-[1px] w-[400px] hover:bg-gray-100 shadow-sm font-medium text-gray-800 flex items-center justify-between p-2 px-6 text-sm"
              :disabled="usersStore.isLoading || disabled || !canEdit"
            >
              <span>{{ comboboxModel ? `${comboboxModel.first_name} ${comboboxModel.last_name}` : 'Select guide to add' }}</span>
              <ChevronsUpDown class="ml-2 h-4 w-4 opacity-50" />
            </ComboboxTrigger>
          </ComboboxAnchor>

          <ComboboxList class="max-w-full w-[400px] h-[20rem] flex flex-col" :is-loading="guidesLoading">
            <!-- Input inside the list -->
            <div class="relative px-2 pt-2">
               <Search class="absolute left-4 top-4 h-4 w-4 text-muted-foreground" />
               <ComboboxInput 
                 placeholder="Search guides..." 
                 v-model="guideSearchQuery"
                 class="w-full h-9 pl-8" 
                 :disabled="disabled"
               />
             </div>

            <ComboboxEmpty>No guides found.</ComboboxEmpty>

            <ComboboxGroup class="overflow-y-auto flex-grow">
              <ComboboxItem
                v-for="guide in availableGuidesForSelect"
                :key="guide.id"
                :value="guide"
              >
                <div class="flex items-center gap-2">
                  <img v-if="guide.avatar_image" :src="guide.avatar_image" class="w-5 h-5 rounded-full object-cover shrink-0" />
                  <div v-else class="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center shrink-0">
                    <UserRoundIcon class="w-3 h-3 text-gray-600" />
                  </div>
                  <span>{{ guide.first_name }} {{ guide.last_name }} ({{ guide.email }})</span>
                </div>
                <ComboboxItemIndicator>
                  <Check class="ml-auto h-4 w-4" />
                </ComboboxItemIndicator>
              </ComboboxItem>
            </ComboboxGroup>
          </ComboboxList>
        </Combobox>
        <FormMessage />
      </FormItem>
    </FormField>

    <!-- Selected Guides List -->
    <div>
      <div class="flex items-center gap-2 mb-2" v-if="canEdit">
        <h3 class="text-md font-medium">
          Added Guides
        </h3>
      </div>
      <div
        v-if="modelValue.length > 0"
        class="space-y-2 overflow-y-auto max-h-[20rem] mt-4"
      >
        <div
          v-for="guide in modelValue"
          :key="guide.id"
          class="flex items-center hover:bg-gray-100 rounded cursor-default h-7"
        >
          <img v-if="guide.avatar_image" :src="guide.avatar_image" class="w-5 h-5 rounded-full mr-2 object-cover" />
          <div v-else class="w-5 h-5 rounded-full mr-2 bg-gray-200 flex items-center justify-center">
            <UserRoundIcon class="w-3 h-3 text-gray-600" />
          </div>
          <span class="flex-grow text-sm">{{ guide.first_name }} {{ guide.last_name }}</span>
          <Button variant="ghost" size="xs" @click="removeGuide(guide.id)" :disabled="disabled" v-if="canEdit">
            <XIcon class="w-3 h-3" />
          </Button>
        </div>
      </div>
      <p v-else class="text-sm text-gray-500">No guides added yet.</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useUsersStore } from '@/stores/users'
import { storeToRefs } from 'pinia'
import type { User } from '@/types/user'
import { Check, ChevronsUpDown, Search, UserRoundIcon, XIcon } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { FormField, FormItem, FormMessage } from '@/components/ui/form'
import {
  Combobox,
  ComboboxAnchor,
  ComboboxTrigger,
  ComboboxItemIndicator,
  ComboboxInput,
  ComboboxList,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxItem,
} from '@/components/ui/combobox'
import { useAuth } from '@/composables/useAuth'

const { isAdmin } = useAuth()

const props = withDefaults(
  defineProps<{
    modelValue: User[]
    disabled?: boolean
    canEdit?: boolean
  }>(),
  { disabled: false, canEdit: undefined }
)

const canEdit = computed(() => props.canEdit ?? isAdmin.value)

const emit = defineEmits(['update:modelValue'])

const usersStore = useUsersStore()
const { guides, guidesLoading } = storeToRefs(usersStore)

onMounted(async () => {
  if (guides.value.length === 0) {
    await usersStore.fetchGuides()
  }
})

const guideSearchQuery = ref('')
const comboboxModel = ref<User | null>(null) // Model for the Combobox

const availableGuidesForSelect = computed(() => {
  const query = guideSearchQuery.value.toLowerCase()

  return (guides.value as User[])
    .filter(guide => !props.modelValue.some(g => g.id === guide.id)) // Exclude already selected guides
    .filter(guide => {
      if (!query) return true
      const fullName = `${guide.first_name} ${guide.last_name}`.toLowerCase()
      const email = guide.email?.toLowerCase() || ''
      return fullName.includes(query) || email.includes(query)
    })
})

const handleGuideSelection = (selectedValue: any) => {
  if (props.disabled) {
    return
  }
  if (typeof selectedValue === 'object' && selectedValue !== null && selectedValue.id && !props.modelValue.some(guide => guide.id === selectedValue.id)) {
    emit('update:modelValue', [...props.modelValue, selectedValue])
  }
  comboboxModel.value = null
  guideSearchQuery.value = ''
}

const removeGuide = (guideId: number) => {
  if (props.disabled) {
    return
  }
  const newSelectedGuides = props.modelValue.filter((guide) => guide.id !== guideId)
  emit('update:modelValue', newSelectedGuides)
}
</script> 